import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { languageType, Message } from "utils/interfaces";
import ChatMessage from "./DemoChatMessage";

interface ChatMessagesParams {
  messages: Message[];
  showLoading: boolean;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  returnToIDE: () => void;
  moveToNextLesson: () => void;
  language: languageType | null;
  nameInitials: string;
}

const MULTIPLE_MESSAGES_TRANSITION = {
  duration: 0.5,
  ease: "linear",
};

const ChatMessages: React.FC<ChatMessagesParams> = ({
  messages,
  showLoading,
  AIAssistant,
  returnToIDE,
  moveToNextLesson,
  language,
  nameInitials,
}) => {
  // States definition
  const [width, setWidth] = useState(767);
  const [multipleMessages, setMultipleMessages] = useState(false);

  // References
  const widthRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMultipleMessages(messages && messages.length > 1);
  }, [messages.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [showLoading, setMultipleMessages, messages]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (widthRef.current) {
        const { width } = widthRef.current.getBoundingClientRect();
        if (width !== undefined && width !== 0) {
          setWidth(width);
        }
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-full flex flex-col items-center overflow-y-auto custom-scroll overflow-x-hidden rounded-sm"
    >
      <div className="max-w-[767px] flex flex-col items-center relative">
        {/* Intro message */}
        <div className="self-start w-full space-y-2 mb-6 text-sm text-neutral-700">
          <div>{AIAssistant.greeting}</div>
          <div>{AIAssistant.intro}</div>
          <div>How can I help you with your coding today?</div>
        </div>
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <ChatMessage
              index={index}
              AIAssistant={AIAssistant}
              message={message}
              returnToIDE={returnToIDE}
              moveToNextLesson={moveToNextLesson}
              language={language}
              nameInitials={nameInitials}
            />
          </React.Fragment>
        ))}
        <AnimatePresence>
          {showLoading && (
            <motion.div
              className="flex w-full pl-4 mb-8"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -50, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <div className="relative w-12 h-12">
                {/* The avatar image */}
                <motion.img
                  src={AIAssistant.img}
                  alt={AIAssistant.name}
                  className="w-12 h-12 rounded-full z-10 relative"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Constant inner border */}
                <motion.div
                  className="absolute -inset-1 rounded-full border-4 border-secondary-300"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                ></motion.div>

                {/* Pulsing outer glow with Framer Motion */}
                <motion.div
                  className="absolute -inset-2 rounded-full border-4 border-secondary-300"
                  animate={{
                    opacity: [0.8, 0.2, 0.8],
                    scale: [1, 0.95, 1],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    ease: "easeInOut",
                  }}
                ></motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatMessages;
